import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Progress } from '../components/ui/progress';
import { 
  BookOpen, 
  Clock, 
  CheckCircle,
  ArrowRight,
  Trophy,
  Spinner
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function MyLearning() {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEnrollments();
  }, []);

  const fetchEnrollments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/my-enrollments`, {
        withCredentials: true
      });
      setEnrollments(response.data);
    } catch (error) {
      console.error('Error fetching enrollments:', error);
    } finally {
      setLoading(false);
    }
  };

  const activeEnrollments = enrollments.filter(e => !e.completed);
  const completedEnrollments = enrollments.filter(e => e.completed);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-20">
          <Spinner size={48} className="animate-spin text-[#2C3E50]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl" data-testid="my-learning-page">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#1A252F] mb-2">Meu Aprendizado</h1>
        <p className="text-[#5C6B7A] mb-8">Acompanhe seu progresso e continue de onde parou</p>

        {enrollments.length === 0 ? (
          <div className="bg-white rounded-xl border border-[#2C3E50]/10 p-12 text-center">
            <BookOpen size={64} className="mx-auto text-[#BDC3C7] mb-4" />
            <h2 className="text-xl font-semibold text-[#1A252F] mb-2">Nenhum curso ainda</h2>
            <p className="text-[#5C6B7A] mb-6">Comece sua jornada de aprendizado matriculando-se em um curso</p>
            <Link to="/courses">
              <button className="bg-[#2C3E50] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#1A252F] transition-colors">
                Ver Cursos
              </button>
            </Link>
          </div>
        ) : (
          <>
            {/* Active Courses */}
            {activeEnrollments.length > 0 && (
              <section className="mb-12">
                <h2 className="text-lg font-semibold text-[#1A252F] mb-4 flex items-center gap-2">
                  <Clock size={20} className="text-[#F39C12]" />
                  Em Andamento ({activeEnrollments.length})
                </h2>
                <div className="grid gap-4">
                  {activeEnrollments.map((enrollment) => (
                    <div 
                      key={enrollment.enrollment_id}
                      className="bg-white rounded-xl border border-[#2C3E50]/10 p-6 hover:shadow-md transition-shadow"
                      data-testid={`enrollment-${enrollment.course_id}`}
                    >
                      <div className="flex flex-col sm:flex-row gap-4">
                        <img 
                          src={enrollment.course_thumbnail || 'https://images.unsplash.com/photo-1765438868883-43efce4fd145?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjB0cmFpbmluZyUyMHNlc3Npb258ZW58MHx8fHwxNzc1MzUxNTk2fDA&ixlib=rb-4.1.0&q=85'}
                          alt={enrollment.course_title}
                          className="w-full sm:w-32 h-24 object-cover rounded-lg"
                        />
                        <div className="flex-1">
                          <h3 className="font-semibold text-[#1A252F] mb-2">{enrollment.course_title}</h3>
                          <div className="mb-3">
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-[#5C6B7A]">Progresso</span>
                              <span className="font-medium text-[#27AE60]">
                                {Math.round(enrollment.progress_percent)}%
                              </span>
                            </div>
                            <Progress value={enrollment.progress_percent} className="h-2" />
                          </div>
                          <Link to={`/courses/${enrollment.course_id}`}>
                            <button 
                              className="text-[#2C3E50] font-medium hover:text-[#27AE60] transition-colors flex items-center gap-1"
                              data-testid={`continue-${enrollment.course_id}`}
                            >
                              Continuar Aprendendo
                              <ArrowRight size={16} />
                            </button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Completed Courses */}
            {completedEnrollments.length > 0 && (
              <section>
                <h2 className="text-lg font-semibold text-[#1A252F] mb-4 flex items-center gap-2">
                  <Trophy size={20} className="text-[#27AE60]" />
                  Concluídos ({completedEnrollments.length})
                </h2>
                <div className="grid gap-4">
                  {completedEnrollments.map((enrollment) => (
                    <div 
                      key={enrollment.enrollment_id}
                      className="bg-white rounded-xl border border-[#27AE60]/20 p-6"
                      data-testid={`completed-${enrollment.course_id}`}
                    >
                      <div className="flex flex-col sm:flex-row gap-4 items-center">
                        <img 
                          src={enrollment.course_thumbnail || 'https://images.unsplash.com/photo-1765438868883-43efce4fd145?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjB0cmFpbmluZyUyMHNlc3Npb258ZW58MHx8fHwxNzc1MzUxNTk2fDA&ixlib=rb-4.1.0&q=85'}
                          alt={enrollment.course_title}
                          className="w-full sm:w-24 h-20 object-cover rounded-lg"
                        />
                        <div className="flex-1 text-center sm:text-left">
                          <h3 className="font-semibold text-[#1A252F] mb-1">{enrollment.course_title}</h3>
                          <div className="flex items-center gap-2 justify-center sm:justify-start text-[#27AE60]">
                            <CheckCircle size={18} weight="fill" />
                            <span className="text-sm font-medium">Concluído</span>
                          </div>
                        </div>
                        <Link to={`/courses/${enrollment.course_id}`}>
                          <button className="bg-[#27AE60] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#219653] transition-colors">
                            Ver Certificado
                          </button>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

export default MyLearning;
